import { useState } from 'react';
import { useWeather, getWeatherInfo, getWindDirectionText } from '../../hooks/useWeather.js';

interface WeatherBadgeProps {
  lat: number | null;
  lng: number | null;
}

export function WeatherBadge({ lat, lng }: WeatherBadgeProps) {
  const { weather, loading } = useWeather(lat, lng);
  const [expanded, setExpanded] = useState(false);

  if (lat === null || lng === null) return null;
  if (!loading && weather === null) return null;

  const c = weather?.current;

  const formatHour = (isoTime: string) => `${parseInt(isoTime.slice(11, 13), 10)}時`;
  const formatPrecip = (prob: number) => (prob < 5 ? '—' : `${prob}%`);

  return (
    <div className="relative">
      <button
        className="bg-white/90 backdrop-blur-sm rounded-full px-3 py-1.5 shadow-md flex items-center gap-1.5 active:bg-gray-50"
        onClick={() => setExpanded((v) => !v)}
        aria-label="現在地の天気"
        type="button"
      >
        {loading && !weather ? (
          <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
        ) : c ? (
          <>
            <span className="text-base leading-none">{getWeatherInfo(c.weatherCode).emoji}</span>
            <span className="text-sm font-semibold text-gray-800">
              {Math.round(c.temperature)}°
            </span>
            <span className="text-xs text-gray-500">
              {getWindDirectionText(c.windDirection)} {Math.round(c.windSpeed)}km/h
            </span>
          </>
        ) : null}
      </button>

      {expanded && c && (
        <>
          {/* 背景タップで閉じる */}
          <div className="fixed inset-0 z-[1099]" onClick={() => setExpanded(false)} />

          <div className="absolute top-full right-0 mt-2 z-[1100] bg-white rounded-2xl shadow-lg overflow-hidden w-[280px]">
            {/* 現在の天気 */}
            <div className="px-4 py-3 flex items-center gap-2.5 border-b border-gray-100">
              {/* 左: 絵文字 + 天気名 */}
              <span className="text-2xl leading-none shrink-0">
                {getWeatherInfo(c.weatherCode).emoji}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 leading-tight">
                  {getWeatherInfo(c.weatherCode).label}
                </p>
                <p className="text-[10px] text-gray-400 mt-0.5">現在地の天気</p>
              </div>
              {/* 右: 気温・風 */}
              <div className="shrink-0 text-xs space-y-0.5">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-gray-400 w-[2em] shrink-0">気温</span>
                  <span className="text-gray-800 font-medium whitespace-nowrap">
                    {Math.round(c.temperature)}°
                    <span className="text-gray-400 font-normal ml-1">
                      体感 {Math.round(c.apparentTemperature)}°
                    </span>
                  </span>
                </div>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-gray-400 w-[2em] shrink-0">風</span>
                  <span className="text-gray-800 font-medium whitespace-nowrap">
                    {getWindDirectionText(c.windDirection)} {Math.round(c.windSpeed)}km/h
                  </span>
                </div>
              </div>
            </div>

            {/* 予報（5時間・縦並び2行構成） */}
            {weather.hourly.length > 0 && (
              <div className="border-t border-gray-100 divide-y divide-gray-50">
                {weather.hourly.map((h) => {
                  const info = getWeatherInfo(h.weatherCode);
                  const precip = formatPrecip(h.precipitationProbability);
                  return (
                    <div key={h.time} className="px-4 py-2">
                      {/* 1行目: 時刻 + 絵文字 + 天気名称 */}
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm text-gray-400 font-medium w-8 shrink-0">
                          {formatHour(h.time)}
                        </span>
                        <span className="text-lg leading-none">{info.emoji}</span>
                        <span className="text-sm font-medium text-gray-700">{info.label}</span>
                      </div>
                      {/* 2行目: 気温 + 体感 + 降水確率 + 風 */}
                      <div className="flex items-center gap-2 mt-0.5 pl-[2.375rem] text-xs text-gray-500 flex-nowrap">
                        <span className="font-semibold text-gray-800 shrink-0 whitespace-nowrap">
                          {Math.round(h.temperature)}°
                        </span>
                        <span className="text-gray-400 shrink-0 whitespace-nowrap">
                          体感 {Math.round(h.apparentTemperature)}°
                        </span>
                        <span
                          className={`shrink-0 whitespace-nowrap ${h.precipitationProbability >= 50 ? 'text-blue-500 font-medium' : ''}`}
                        >
                          {precip}
                        </span>
                        <span className="shrink-0 whitespace-nowrap">
                          {getWindDirectionText(h.windDirection)} {Math.round(h.windSpeed)}km/h
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
